(() => {
  const GWP_RULE = {
    version: "2026-05-22-no-cart-reload",
    enabled: false,
    ruleId: "barn-plaque-2026-05",
    thresholdCents: 12000,
    giftProductHandle: "small-plaque-barn",
    giftSku: "536 CBN-1"
  };

  if (!GWP_RULE.enabled || window.__hadleyGwpCartReconcilerLoaded) {
    return;
  }
  window.__hadleyGwpCartReconcilerLoaded = true;

  let giftVariantPromise = null;
  let reconcilePromise = null;
  let reconcileTimer = 0;
  let checkoutNavigationStarted = false;

  function shopifyRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  function isGiftLine(item) {
    return String(item?.sku || "").trim() === GWP_RULE.giftSku;
  }

  function isAutoGiftLine(item) {
    return isGiftLine(item) && item?.properties?._gwp_rule === GWP_RULE.ruleId;
  }

  function collectBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections }
    }));
    return sections;
  }

  async function fetchCartJson() {
    const response = await fetch(`${shopifyRoot()}cart.js`, {
      headers: { "Accept": "application/json" },
      cache: "no-store"
    });
    if (!response.ok) {
      throw new Error("gwp_cart_fetch_failed");
    }
    return response.json();
  }

  async function fetchGiftVariant() {
    if (!giftVariantPromise) {
      giftVariantPromise = fetch(`${shopifyRoot()}products/${GWP_RULE.giftProductHandle}.js`, {
        headers: { "Accept": "application/json" },
        cache: "no-store"
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("gwp_product_fetch_failed");
          }
          return response.json();
        })
        .then((product) => {
          const variants = Array.isArray(product?.variants) ? product.variants : [];
          return variants.find((variant) =>
            String(variant?.sku || "").trim() === GWP_RULE.giftSku && variant?.available === true
          ) || null;
        })
        .catch((error) => {
          console.warn("[Hadley GWP]", error);
          return null;
        });
    }
    return giftVariantPromise;
  }

  function eligibleSubtotalCents(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (isGiftLine(item)) {
        return sum;
      }
      return sum + Number(item?.final_line_price || 0);
    }, 0);
  }

  function dispatchCartChange(cart, baseEvent = "gwp:reconcile") {
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent,
        cart
      }
    }));
  }

  function refreshAfterMutation(cart) {
    dispatchCartChange(cart);
  }

  async function changeLineQuantity(lineKey, quantity, options) {
    const sections = collectBundledSections();
    const response = await fetch(`${shopifyRoot()}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        id: lineKey,
        quantity,
        sections
      })
    });
    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart?.description || "gwp_cart_change_failed");
    }
    refreshAfterMutation(cart);
    return cart;
  }

  async function addGiftLine(variantId, options) {
    const sections = collectBundledSections();
    const formData = new FormData();
    formData.set("id", String(variantId));
    formData.set("quantity", "1");
    formData.set("properties[_gwp]", "threshold");
    formData.set("properties[_gwp_rule]", GWP_RULE.ruleId);
    formData.set("properties[_gwp_threshold]", String(GWP_RULE.thresholdCents));
    formData.set("sections", sections.join(","));
    formData.set("sections_url", window.location.pathname);

    const response = await fetch(`${shopifyRoot()}cart/add.js`, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: formData
    });
    const added = await response.json();
    if (!response.ok) {
      throw new Error(added?.description || "gwp_cart_add_failed");
    }
    const cart = await fetchCartJson();
    cart.sections = added.sections || {};
    refreshAfterMutation(cart);
    return cart;
  }

  async function reconcileCart(options = {}) {
    if (checkoutNavigationStarted) {
      return false;
    }
    if (reconcilePromise) {
      return reconcilePromise;
    }

    reconcilePromise = (async () => {
      const cart = await fetchCartJson();
      const giftLines = (cart.items || []).filter(isGiftLine);
      const autoGiftLines = giftLines.filter(isAutoGiftLine);
      const manualGiftLines = giftLines.filter((item) => !isAutoGiftLine(item));
      const eligible = eligibleSubtotalCents(cart) >= GWP_RULE.thresholdCents;
      let changed = false;

      if (!eligible) {
        for (const line of autoGiftLines) {
          await changeLineQuantity(line.key, 0, options);
          changed = true;
        }
        return changed;
      }

      if (manualGiftLines.length > 0) {
        for (const line of autoGiftLines) {
          await changeLineQuantity(line.key, 0, options);
          changed = true;
        }
        return changed;
      }

      if (autoGiftLines.length > 0) {
        const [firstAutoGift, ...extraAutoGifts] = autoGiftLines;
        if (Number(firstAutoGift.quantity || 0) !== 1) {
          await changeLineQuantity(firstAutoGift.key, 1, options);
          changed = true;
        }
        for (const line of extraAutoGifts) {
          await changeLineQuantity(line.key, 0, options);
          changed = true;
        }
        return changed;
      }

      const variant = await fetchGiftVariant();
      if (!variant?.id) {
        return false;
      }
      await addGiftLine(variant.id, options);
      return true;
    })()
      .catch((error) => {
        console.warn("[Hadley GWP]", error);
        return false;
      })
      .finally(() => {
        reconcilePromise = null;
      });

    return reconcilePromise;
  }

  function scheduleReconcile() {
    if (checkoutNavigationStarted) {
      return;
    }
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => {
      void reconcileCart();
    }, 120);
  }

  async function debugStatus() {
    const cart = await fetchCartJson();
    const giftVariant = await fetchGiftVariant();
    const giftLines = (cart.items || []).filter(isGiftLine);
    return {
      rule: GWP_RULE,
      cartItemCount: cart.item_count,
      eligibleSubtotalCents: eligibleSubtotalCents(cart),
      eligible: eligibleSubtotalCents(cart) >= GWP_RULE.thresholdCents,
      giftVariant,
      giftLines,
      autoGiftLines: giftLines.filter(isAutoGiftLine),
      manualGiftLines: giftLines.filter((item) => !isAutoGiftLine(item))
    };
  }

  window.HadleyGwpCartReconciler = {
    rule: GWP_RULE,
    reconcile: reconcileCart,
    debug: debugStatus
  };

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('[name="checkout"], a[href*="/checkout"]')) {
      return;
    }
    checkoutNavigationStarted = true;
    window.clearTimeout(reconcileTimer);
  }, true);

  document.addEventListener("cart:change", (event) => {
    if (event.detail?.baseEvent === "gwp:reconcile") {
      return;
    }
    scheduleReconcile();
  });
  document.addEventListener("cart:refresh", scheduleReconcile);
  document.addEventListener("variant:add", scheduleReconcile);
  window.addEventListener("pageshow", scheduleReconcile);
  document.addEventListener("DOMContentLoaded", scheduleReconcile);
})();
